import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isNative, platform } from "@/lib/platform";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const handled = useRef(false);
  const [showFallback, setShowFallback] = useState(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      navigate("/auth", { replace: true });
      return;
    }
    const session_id = decodeURIComponent(m[1]);

    // ANDROID BRIDGE: when this page is loaded inside the in-app Browser tab
    // opened by the native Android Google flow, we need to hand the session_id
    // back to the native app. We do this by setting window.location to a
    // custom deep-link URL — the OS intercepts it (via the intent-filter we
    // registered in AndroidManifest.xml) and routes it to Hush, which fires
    // an `appUrlOpen` event in @capacitor/app.
    //
    // The native app's listener (see lib/platform.signInWithGoogleAndroid)
    // dismisses the browser, exchanges the session_id, and proceeds.
    //
    // On the regular web (apphush.online opened in a normal browser), this
    // deep-link nav silently fails — most browsers handle the unknown scheme
    // by ignoring it — and we fall through to the normal /auth/google call.
    //
    // We also handle the case where this page is loaded INSIDE the Capacitor
    // WebView itself (rare — user manually visits /auth/callback while in
    // the app) — in that case Capacitor.isNativePlatform() is true so we
    // skip the bridge and just do the normal /auth/google exchange.
    const inAppWebView = isNative();
    if (!inAppWebView) {
      // Web or in-Browser-tab path. Try the deep link as a side-effect.
      // If the OS intercepts it, the user never sees this page complete.
      // If not, we fall through to the web /auth/google flow below.
      try {
        const deepLink = `app.hush.journal://auth-callback#session_id=${encodeURIComponent(session_id)}`;
        // Use a hidden iframe so failure-to-intercept doesn't break the
        // normal back-button history (window.location replacement would).
        const iframe = document.createElement("iframe");
        iframe.style.display = "none";
        iframe.src = deepLink;
        document.body.appendChild(iframe);
        // Clean up after 1s
        setTimeout(() => { try { iframe.remove(); } catch (_) { /* noop */ } }, 1000);
      } catch (_) { /* deep link failure is non-fatal */ }
    }

    // Regular web/PWA flow — exchange session_id immediately. If the deep
    // link succeeded the user is no longer on this page so this is a no-op.
    (async () => {
      try {
        const { data } = await api.post("/auth/google", { session_id });
        setUser(data);
        window.history.replaceState(null, "", "/app");
        navigate("/app", { replace: true });
      } catch {
        // Show a fallback UI with an "Open in Hush app" link so users on
        // Android who installed the app but landed here in a desktop browser
        // can still complete sign-in.
        setShowFallback(true);
        setTimeout(() => {
          navigate("/auth?error=google", { replace: true });
        }, 4000);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-bone-100" data-testid="auth-callback">
      <div className="text-ink-500 font-serif text-lg text-center px-4">
        {showFallback ? (
          <>
            <p>Continuing in the Hush app…</p>
            <p className="text-sm mt-2">If nothing happens, install Hush from{" "}
              {platform() === "android" ? "Play Store" : "App Store"}.
            </p>
          </>
        ) : (
          "Signing you in…"
        )}
      </div>
    </div>
  );
}
